import { useCallback, useEffect, useMemo, useRef } from 'react';

import { TabType } from '@/components/Types';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { ActionTypeManageFCupEnum } from '@/services/friendsCup';
import {
  requestPostulationMl,
  requestPostulantManagementMl,
} from '@/services/minileagues';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { ERROR_STATUS_FCUP, TEXTS } from '@/utils';

export const useHandleInvitations = (
  list: GenericItemTourneysData[] | null,
  tab = TabType.FirstTab
) => {
  const toast = useToast();
  const { tourneyData, updateTourney } = useTourney();
  const listRef = useRef<GenericItemTourneysData[] | null>(null);

  useEffect(() => {
    if (!list) return;
    listRef.current = list;
  }, [list]);

  const textComplementary = useMemo(() => {
    return tab === TabType.FirstTab ? 'a la Miniliga' : 'a la Miniliga de';
  }, [tab]);

  const onHandlePendingPostulation = useCallback(
    async (id: number, name: string) => {
      try {
        const responseManagePostulant = await requestPostulantManagementMl({
          tipoAccion: ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
          idMiniLiga: id,
        });

        if (responseManagePostulant.estado) {
          if (tab === TabType.FirstTab) {
            toast.show(
              `Tenías una invitación pendiente para la Miniliga ${name}. Ya estás participando.`
            );
          } else {
            toast.show(
              `Tenías una invitación pendiente para la Miniliga de ${name}. Ya estás participando.`
            );
          }
        } else {
          console.error(responseManagePostulant.mensaje);
          toast.show('Error al invitar');
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      }
    },
    [toast, tab]
  );

  const onHandleSubmit = useCallback(
    async ({ id }: ActionsTourneys) => {
      try {
        if (!listRef.current) return;

        const group = listRef.current.find((t) => t.id === id);
        if (!group) return; // Add check for group existence

        const response = await requestPostulationMl(id);

        if (response.estado) {
          toast.show(
            `Enviaste tu solicitud para unirte ${textComplementary} ${group?.title}`
          );
          updateTourney({
            idsPendings: [...(tourneyData.idsPendings || []), id],
          });
        } else {
          if (
            response.codError &&
            response.codError === ERROR_STATUS_FCUP.PENDING_INVITATION
          ) {
            await onHandlePendingPostulation(id, String(group.title));
            updateTourney({
              idsPlayings: [...(tourneyData.idsPlayings || []), id],
            });
          } else if (
            response.mensaje &&
            response.codError !== ERROR_STATUS_FCUP.PENDING_INVITATION
          ) {
            toast.show(response.mensaje);
          }
        }
      } catch (err) {
        toast.show(TEXTS.defaultError);
        throw new Error(TEXTS.defaultError);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onHandlePendingPostulation,
      updateTourney,
      tourneyData,
      textComplementary,
      toast,
    ]
  );

  return { onHandleSubmit };
};
